import i18n from '@/localisation/i18n';
import { HeaderInfo } from '@/UI/HeaderInfo';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useLocation, useParams } from 'react-router-dom';
import {
  getTestsQuestions,
  ResponseGetTestsQuestions,
} from './api/getTestsQuestions';
import GaugeChart from './Gauge/GaugeChart';
import { getTestsScore, ResponseGetTestScoreData } from './api/getTestsScore';
import { ResultItem } from './components/ResultItem';
import { getTestsData, ResponseGetTestsData } from './api/getTestsData';
import { useTranslation } from 'react-i18next';

export const TestResult = () => {
  const { i18n: i18nInstance } = useTranslation();
  const location = useLocation();
  const state = (location.state as { levels?: any; title?: string } | null) || {};
  const { client, id, type } = useParams();

  const { data: testData } = useQuery<ResponseGetTestsData>({
    queryKey: ['tests-data', i18nInstance.language],
    staleTime: 60 * 60 * 1000,
    queryFn: () => getTestsData({ lang: i18nInstance.language }),
    enabled: !state.levels || !state.title,
  });

  const { data: res, isLoading: isScoreLoading } =
    useQuery<ResponseGetTestScoreData>({
      queryKey: ['testInner', id, client],
      staleTime: 60 * 60 * 1000,
      queryFn: () => getTestsScore({ user: client!, id: id! }),
    });

  const { data, isLoading: isQuestionsLoading } =
    useQuery<ResponseGetTestsQuestions>({
      queryKey: ['questions', i18nInstance.language, type],
      staleTime: 60 * 60 * 1000,
      queryFn: () =>
        getTestsQuestions({ type: type!, lang: i18nInstance.language }),
    });

  const currentTest = testData?.find(
    t => t.id.toLowerCase() === type?.toLowerCase()
  );
  const levels = state.levels || currentTest?.levels || {};
  const title = state.title || currentTest?.title || data?.title || '';

  if (isScoreLoading || isQuestionsLoading) {
    return (
      <HeaderInfo title={title}>
        <div className="flex h-64 items-center justify-center">
          <span className="loading loading-spinner loading-lg text-primary"></span>
        </div>
      </HeaderInfo>
    );
  }

  if (!data || !res) {
    return (
      <HeaderInfo title={title}>
        <div className="mt-8 text-center text-gray-500">
          {i18n.t('no.records')}
        </div>
      </HeaderInfo>
    );
  }

  const resultText = data?.result?.find(
    element => res.score <= element.rangeBefore
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
    >
      <HeaderInfo title={title}>
        <div className="mt-8">
          <div className="mb-6">
            <GaugeChart score={res.score} type={type!} levels={levels} />
          </div>

          {resultText && (
            <>
              <h3 className="mb-4 text-center text-xl font-bold">
                {resultText.title}
              </h3>
              <div className="mb-4">{resultText.text}</div>
            </>
          )}

          {Boolean(data.steps?.length) && (
            <>
              <h3 className="mb-4 font-bold">{i18n.t('questions')}</h3>
              <ol style={{ listStyle: 'decimal' }}>
                {data.steps.map((step, index) => (
                  <ResultItem
                    key={id! + index}
                    index={index}
                    step={step}
                    scoreList={res.scoreList}
                  />
                ))}
              </ol>
            </>
          )}
        </div>
      </HeaderInfo>
    </motion.div>
  );
};
